import LogoWhite from '@/../../public/storage/Logo-White.webp';
import Logo from '@/../../public/storage/Logo.webp';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { PageProps } from '@/types';
import { Link, router, usePage } from '@inertiajs/react';
import { LogOut, Menu } from 'lucide-react';
import { PropsWithChildren, useEffect, useState } from 'react';

import { NavLink, Routes } from './AdminRoutes';

const SideNavLink = ({
  link,
  onNavigate,
}: {
  link: Omit<NavLink, 'sublinks'>;
  onNavigate: () => void;
}) => {
  const isActive = route()
    .current()
    ?.startsWith(link.routeName! + '.');

  return (
    <Button
      asChild
      variant="ghost"
      className={cn(
        'w-full justify-start rounded-none border-l-2 pl-3.5',
        isActive
          ? 'border-primary bg-secondary/10'
          : 'border-transparent hover:text-secondary hover:bg-secondary/10'
      )}
      onClick={onNavigate}
    >
      <Link
        href={route(link.routeName! + (link.index ?? '.index'))}
        aria-current={isActive ? 'page' : undefined}
      >
        {link.label}
      </Link>
    </Button>
  );
};

const SideBar = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose?: () => void;
}) => {
  return (
    <aside
      className={cn(
        'fixed z-40 w-72 lg:static flex grow-0 shrink-0 h-screen bg-ink transition-all duration-300',
        open ? 'ml-0' : '-ml-72'
      )}
    >
      <div className="flex flex-col size-full overflow-hidden">
        <div className="w-full py-4 flex items-center justify-center">
          <Link href={route('admin.dashboard.index')} onClick={onClose}>
            <img
              className="w-48 h-auto object-cover"
              alt="Samuthrika Academy Logo"
              src={LogoWhite}
            />
          </Link>
        </div>
        <div className="py-4 grid text-ink-foreground overflow-hidden">
          <nav className="flex flex-col overflow-x-hidden">
            <Accordion
              type="single"
              collapsible
              className="flex-1 w-full overflow-y-auto scrollbar-gutter-stable"
            >
              <ul className="flex flex-col px-3 py-2 space-y-1">
                {Routes.map((link) => (
                  <li key={link.label}>
                    {link.sublinks && link.sublinks.length > 0 ? (
                      <AccordionItem value={link.label} className="border-none">
                        <AccordionTrigger className="shrink-0 pl-4 text-sm pr-4 py-2 h-10 text-ink-foreground underline-offset-4 [&>svg]:size-5">
                          <span className="inline-flex">{link.label}</span>
                        </AccordionTrigger>
                        <AccordionContent className="py-2">
                          <ul className="flex flex-col ml-4 pl-2 border-l border-ink-foreground/20 space-y-1">
                            {link.sublinks.map((sublink) => (
                              <li key={sublink.label}>
                                <SideNavLink
                                  link={sublink}
                                  onNavigate={onClose ?? (() => undefined)}
                                />
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ) : (
                      <SideNavLink
                        link={link}
                        onNavigate={onClose ?? (() => undefined)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </Accordion>
          </nav>
        </div>
      </div>
    </aside>
  );
};

const AdminLayout = ({ children }: PropsWithChildren) => {
  const { user } = usePage<PageProps>().props.auth;
  const isLarge = useMediaQuery('(min-width: 1024px)');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    setSidebarOpen(isLarge);
  }, [isLarge]);

  return (
    <div className="h-screen flex overflow-hidden">
      {!isLarge && (
        <div
          className={cn(
            'fixed inset-0 z-40 bg-ink/80',
            sidebarOpen
              ? 'animate-in fade-in-0'
              : 'animate-out fade-out-0 hidden'
          )}
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <SideBar
        open={sidebarOpen}
        onClose={isLarge ? undefined : () => setSidebarOpen(false)}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="h-16 flex shrink-0 px-4 md:px-6 py-2 bg-card/90 backdrop-blur-sm transition-colors duration-500 supports-backdrop-blur:bg-card/60">
          <div className="flex-1 flex items-center justify-between">
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <Menu />
              </Button>
              {!sidebarOpen && (
                <Link href={route('admin.dashboard.index')}>
                  <img
                    className="h-12 w-auto object-cover"
                    alt="Samuthrika Academy"
                    src={Logo}
                    loading="lazy"
                  />
                </Link>
              )}
            </div>
            <div className="flex-1 flex justify-end gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Avatar asChild>
                    <AvatarFallback
                      asChild
                      className="hover:bg-primary text-secondary-foreground hover:text-primary-foreground transition-all"
                    >
                      <button>{user.first_name[0]}</button>
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                    onClick={() =>
                      router.post(route('admin.logout'), {}, { replace: true })
                    }
                  >
                    <LogOut size={20} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <main
          className="flex-1 bg-surface px-4 md:px-6 pb-8 space-y-6 overflow-y-auto"
          scroll-region="true"
        >
          <div className="flex-1 flex flex-col">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
